// EDEN governed change card (campaign flight START date).
// PARITY: this file must stay byte-identical between client (Cortex OS) and
// superadmin (Quantum OS).
//
// Shows what the governed planner save says it would change, lets the person
// confirm it once, and reports the result from a read-back of the campaign
// record. It never claims success from the request alone, and it never
// launches, schedules delivery, creates spend or touches a platform: the only
// write it can cause is the existing PATCH /api/media-campaigns/:id the
// person confirms.

import React, { useCallback, useEffect, useRef, useState } from "react";
import "./EdenGovernedChangeCard.css";
import {
  announceCampaignUpdated,
  confirmGovernedChange,
  formatCalendarDate,
  previewGovernedChange,
} from "../eden/core/edenGovernedChange";
import { isCurrentEdenSession, subscribeEdenSession } from "../eden/core/edenSession";

// Terminal outcomes survive the panel unmounting (closing chat, switching to
// full screen), so a confirmed change is never offered for confirmation again.
// They belong to the Eden session that produced them: when it ends (another
// account, sign-out) they are forgotten with the rest of its state.
const OUTCOMES = new Map();
subscribeEdenSession(() => OUTCOMES.clear());

export function resetEdenGovernedChangeOutcomesForTests() {
  OUTCOMES.clear();
}

const TERMINAL = new Set(["verified", "verified_end_moved", "not_saved", "uncertain", "dismissed", "no_change", "blocked"]);

// A card from an ended Eden session (another account signed in, or the session
// ended) shows nothing and sends nothing: it is never previewed or confirmed.
const SESSION_ENDED = "session_ended";

const SCOPE_NOTE =
  "Confirming saves Cphere's planning record only. It does not launch the campaign, schedule delivery, create spend, or change anything at a platform.";
const LIFECYCLE_NOTE =
  "After a save, Cphere re-checks its campaign lifecycle notices for the new date, so campaign contacts may receive a schedule notice if those emails are on.";

// Short verdicts for when the host shows the full outcome sentence in the
// conversation itself (onOutcome), so the result is not printed twice.
const VERDICT_LABELS = {
  verified: "Saved · verified from the campaign record",
  verified_end_moved: "Saved · the end date reads differently — check the planner",
  not_saved: "Not saved",
  uncertain: "Save not confirmed",
  dismissed: "Not changed",
};

function initialState(change) {
  if (!isCurrentEdenSession(change.sessionGeneration)) return { phase: SESSION_ENDED };
  const stored = OUTCOMES.get(change.id);
  if (stored) return stored;
  if (change.state === "proposal") return { phase: "previewing" };
  // The reply text already states a blocked / no-change answer from /ask;
  // the card adds only the next step, never a second copy of the message.
  return { phase: change.state, fromReply: true };
}

function outcomeSentence(change, state) {
  const name = change.campaign?.name || "The campaign";
  const proposed = formatCalendarDate(change.proposedStart, { weekday: true });
  const readStart = formatCalendarDate(state.readback?.flightStartDate, { weekday: true });
  const readEnd = formatCalendarDate(state.readback?.flightEndDate);
  switch (state.phase) {
    case "verified":
      return `Saved and verified: ${name} now starts on ${readStart}, read back from the campaign record. The end date is still ${readEnd}.`;
    case "verified_end_moved":
      return `The start date saved and reads back as ${readStart}, but the end date now reads ${readEnd}. Check the campaign in the Media Planner.`;
    case "not_saved":
      return `Not saved: the campaign record still starts on ${readStart}, not ${proposed}.`;
    case "uncertain":
      return "I couldn't confirm whether the change saved: the save didn't answer and the campaign couldn't be read back. Check the campaign before trying again.";
    case "dismissed":
      return `Not changed: ${name} still starts on ${formatCalendarDate(change.current?.flightStartDate)}.`;
    default:
      return "";
  }
}

export default function EdenGovernedChangeCard({ change, onOutcome, onFollowUp, onNavigate, fetchImpl }) {
  const [state, setState] = useState(() => initialState(change));
  const inFlightRef = useRef(false);
  const mountedRef = useRef(true);
  const reportedRef = useRef(new Set());

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const settle = useCallback(
    (next) => {
      // A result that returns after its Eden session ended is discarded: it
      // must not restore an outcome, a message or a card for the next account.
      if (!isCurrentEdenSession(change.sessionGeneration)) return;
      if (TERMINAL.has(next.phase)) OUTCOMES.set(change.id, next);
      if (mountedRef.current) setState(next);
      const sentence = outcomeSentence(change, next);
      if (sentence && !reportedRef.current.has(next.phase) && typeof onOutcome === "function") {
        reportedRef.current.add(next.phase);
        onOutcome(sentence, { phase: next.phase, changeId: change.id });
      }
    },
    [change, onOutcome]
  );

  const runPreview = useCallback(async () => {
    if (inFlightRef.current) return;
    if (!isCurrentEdenSession(change.sessionGeneration)) {
      if (mountedRef.current) setState({ phase: SESSION_ENDED });
      return;
    }
    inFlightRef.current = true;
    setState({ phase: "previewing" });
    try {
      const result = await previewGovernedChange(change, { fetchImpl });
      settle(result.phase === "error" ? { phase: "preview_error" } : result);
    } finally {
      inFlightRef.current = false;
    }
  }, [change, fetchImpl, settle]);

  useEffect(() => {
    if (state.phase === "previewing" && !inFlightRef.current) runPreview();
    // Preview once per mount of an unresolved proposal.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleConfirm = useCallback(async () => {
    if (inFlightRef.current || state.phase !== "review") return;
    if (!isCurrentEdenSession(change.sessionGeneration)) {
      if (mountedRef.current) setState({ phase: SESSION_ENDED });
      return;
    }
    inFlightRef.current = true;
    const token = state.token;
    const reviewed = state;
    setState({ ...reviewed, phase: "saving" });
    try {
      const result = await confirmGovernedChange(change, token, { fetchImpl });
      if (!isCurrentEdenSession(change.sessionGeneration)) return;
      if (result.phase === "stale") {
        if (mountedRef.current) setState({ phase: "stale" });
        return;
      }
      if (result.phase === "verified" || result.phase === "verified_end_moved") announceCampaignUpdated(change);
      settle({ ...result, changeSet: reviewed.changeSet });
    } finally {
      inFlightRef.current = false;
    }
  }, [change, fetchImpl, settle, state]);

  const handleDismiss = useCallback(() => {
    if (inFlightRef.current) return;
    settle({ phase: "dismissed" });
  }, [settle]);

  const navigateTo = (href) => {
    if (href && typeof onNavigate === "function") onNavigate(href);
  };

  const campaign = change.campaign;
  const phase = state.phase;
  const busy = phase === "previewing" || phase === "saving";
  const blocker = phase === "blocked" ? state.blocker || change.blocker : null;
  const showBlockerMessage = Boolean(
    blocker?.message && (!state.fromReply || blocker.code === "invalid_proposal")
  );

  if (phase === SESSION_ENDED || !isCurrentEdenSession(change.sessionGeneration)) return null;

  // A blocked or no-change answer from /ask is already stated in the reply;
  // without a next step to offer, a card would only repeat its heading.
  if (
    state.fromReply &&
    (phase === "blocked" || phase === "no_change") &&
    !showBlockerMessage &&
    !blocker?.nextAction?.href
  ) {
    return null;
  }

  return (
    <section
      className={`eden-governed-change eden-governed-change--${phase}`}
      aria-label="Governed campaign change"
      aria-busy={busy}
      data-governed-change-phase={phase}
    >
      <div className="eden-governed-change-kicker">
        {change.state === "clarification" ? "Which campaign?" : "Governed change · Start date"}
      </div>

      {campaign ? (
        <div className="eden-governed-change-campaign">
          <span className="eden-governed-change-campaign-name">{campaign.name}</span>
          {campaign.advertiserName ? (
            <span className="eden-governed-change-campaign-advertiser"> · {campaign.advertiserName}</span>
          ) : null}
        </div>
      ) : null}

      {change.state === "clarification" ? (
        <ul className="eden-governed-change-candidates">
          {change.candidates.map((candidate) => (
            <li key={candidate.id}>
              <button
                type="button"
                className="eden-governed-change-candidate"
                onClick={() =>
                  typeof onFollowUp === "function" &&
                  onFollowUp(
                    `Change the start date on campaign #${candidate.id} to ${change.proposedStart || change.requestedText || ""}`.trim()
                  )
                }
              >
                <span className="eden-governed-change-candidate-name">{candidate.name}</span>
                <span className="eden-governed-change-candidate-meta">
                  {formatCalendarDate(candidate.flightStartDate)} – {formatCalendarDate(candidate.flightEndDate)}
                  {candidate.advertiserName ? ` · ${candidate.advertiserName}` : ""}
                </span>
              </button>
            </li>
          ))}
        </ul>
      ) : null}

      {phase === "previewing" ? (
        <p className="eden-governed-change-note" role="status">
          Checking the change with the governed planner save…
        </p>
      ) : null}

      {(phase === "review" || phase === "saving") && state.changeSet ? (
        <>
          <table className="eden-governed-change-table">
            <thead>
              <tr>
                <th scope="col">Field</th>
                <th scope="col">Now</th>
                <th scope="col">After</th>
              </tr>
            </thead>
            <tbody>
              {state.changeSet.changedFields.map((field) => (
                <tr key={field.key}>
                  <th scope="row">{field.label}</th>
                  <td>{field.key === "flightStartDate" ? formatCalendarDate(field.oldValue) : field.oldValue || "Not set"}</td>
                  <td>{field.key === "flightStartDate" ? formatCalendarDate(field.newValue, { weekday: true }) : field.newValue || "Not set"}</td>
                </tr>
              ))}
              {change.current?.flightEndDate ? (
                <tr className="eden-governed-change-unchanged">
                  <th scope="row">Flight end</th>
                  <td>{formatCalendarDate(change.current.flightEndDate)}</td>
                  <td>Unchanged</td>
                </tr>
              ) : null}
            </tbody>
          </table>
          {state.changeSet.operatorGuidance ? (
            <p className="eden-governed-change-note">
              {state.changeSet.editImpact === "UNVERIFIABLE_OPERATOR_DECISION_REQUIRED"
                ? "The strategy was reviewed against the current start date, so saving marks it for an operator decision; the earlier review doesn't cover the new date. "
                : ""}
              {state.changeSet.operatorGuidance}
            </p>
          ) : null}
          <p className="eden-governed-change-note">{SCOPE_NOTE}</p>
          <p className="eden-governed-change-note eden-governed-change-note--quiet">{LIFECYCLE_NOTE}</p>
          <div className="eden-governed-change-actions">
            <button
              type="button"
              className="eden-governed-change-button eden-governed-change-button--secondary"
              onClick={handleDismiss}
              disabled={phase === "saving"}
            >
              Not now
            </button>
            <button
              type="button"
              className="eden-governed-change-button eden-governed-change-button--primary"
              onClick={handleConfirm}
              disabled={phase === "saving"}
            >
              {phase === "saving" ? "Saving…" : "Confirm change"}
            </button>
          </div>
        </>
      ) : null}

      {["verified", "verified_end_moved", "not_saved", "uncertain", "dismissed"].includes(phase) ? (
        <p
          className={`eden-governed-change-result eden-governed-change-result--${phase}`}
          role={phase === "verified" || phase === "dismissed" ? "status" : "alert"}
        >
          {typeof onOutcome === "function" ? VERDICT_LABELS[phase] : outcomeSentence(change, state)}
        </p>
      ) : null}

      {phase === "verified" && state.changeSet?.editImpact === "UNVERIFIABLE_OPERATOR_DECISION_REQUIRED" ? (
        <p className="eden-governed-change-note">
          The strategy is now marked for an operator decision before it is current again.
        </p>
      ) : null}

      {phase === "no_change" && !state.fromReply ? (
        <p className="eden-governed-change-result" role="status">
          {campaign?.name || "The campaign"} already starts on {formatCalendarDate(change.proposedStart || change.current?.flightStartDate, { weekday: true })}. Nothing to change.
        </p>
      ) : null}

      {phase === "stale" ? (
        <div className="eden-governed-change-result" role="alert">
          <p>The campaign changed after this preview, so the confirmation no longer matches it. Nothing was saved.</p>
          <button type="button" className="eden-governed-change-button eden-governed-change-button--secondary" onClick={runPreview}>
            Review the latest change
          </button>
        </div>
      ) : null}

      {phase === "preview_error" ? (
        <div className="eden-governed-change-result" role="alert">
          <p>The governed planner save didn't answer, so nothing was previewed or changed.</p>
          <button type="button" className="eden-governed-change-button eden-governed-change-button--secondary" onClick={runPreview}>
            Try the preview again
          </button>
        </div>
      ) : null}

      {phase === "not_saved" || phase === "uncertain" ? (
        <div className="eden-governed-change-actions">
          <button type="button" className="eden-governed-change-button eden-governed-change-button--secondary" onClick={runPreview}>
            Review again
          </button>
        </div>
      ) : null}

      {blocker && (showBlockerMessage || blocker.nextAction?.href) ? (
        <div className="eden-governed-change-result eden-governed-change-result--blocked" role="status">
          {showBlockerMessage ? <p>{blocker.message}</p> : null}
          {blocker.nextAction?.href ? (
            <button
              type="button"
              className="eden-governed-change-button eden-governed-change-button--secondary"
              onClick={() => navigateTo(blocker.nextAction.href)}
            >
              {blocker.nextAction.label}
            </button>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}
